import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, money, fmtDate, formatApiError, laundryLabels, laundryKeys, fileUrl, mesi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit3, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { useReportLink, MonthChargesTable } from "@/components/ReportLink";
import LavanderiaDocs from "@/components/LavanderiaDocs";

const now = new Date();
const emptyCost = () => ({ month: now.getMonth() + 1, year: now.getFullYear(), apartment_id: "", property_id: "", supplier: "", amount: 0, paid: false, document_id: "", notes: "", charged_to: "proprietario", considera_rendiconto: true, mostra_pdf: true });
const emptyDel = () => ({ date: now.toISOString().slice(0, 10), apartment_id: "", property_id: "", double_sheets: 0, single_sheets: 0, pillowcases: 0, large_towels: 0, medium_towels: 0, bath_mats: 0, notes: "" });

const tabsExtra = 1;
export default function Lavanderia() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("consumo");
  const [apartments, setApartments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [fApt, setFApt] = useState("all");
  const [fProp, setFProp] = useState("all");
  const [consumo, setConsumo] = useState(null);
  const [costs, setCosts] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [openCost, setOpenCost] = useState(false);
  const { propose, dialog: linkDialog } = useReportLink();
  const [openDel, setOpenDel] = useState(false);
  const [formCost, setFormCost] = useState(emptyCost());
  const [formDel, setFormDel] = useState(emptyDel());
  const [editCost, setEditCost] = useState(null);
  const [editDel, setEditDel] = useState(null);

  const load = async () => {
    const [a, p, c, d] = await Promise.all([
      http.get("/apartments"), http.get("/properties"), http.get("/laundry-costs"), http.get("/laundry-deliveries")]);
    setApartments(a.data); setProperties(p.data); setCosts(c.data); setDeliveries(d.data);
  };
  const loadConsumo = async () => {
    const p = { month: Number(month), year: Number(year) };
    if (fApt !== "all") p.apartment_id = fApt;
    if (fProp !== "all") p.property_id = fProp;
    const { data } = await http.get("/laundry/consumption", { params: p });
    setConsumo(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadConsumo(); }, [month, year, fApt, fProp]);

  useEffect(() => {
    const n = params.get("new");
    if (n === "costo") { setFormCost(emptyCost()); setEditCost(null); setOpenCost(true); }
    if (n === "consegna") { setFormDel(emptyDel()); setEditDel(null); setOpenDel(true); }
    if (n) { params.delete("new"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const saveCost = async (e) => {
    e.preventDefault();
    const p = { ...formCost, month: Number(formCost.month), year: Number(formCost.year), amount: Number(formCost.amount) || 0 };
    ["apartment_id", "property_id", "document_id"].forEach((k) => { if (!p[k]) p[k] = null; });
    try {
      let saved;
      if (editCost) saved = (await http.put(`/laundry-costs/${editCost}`, p)).data; else saved = (await http.post("/laundry-costs", p)).data;
      toast.success("Salvato"); setOpenCost(false); load();
      if (saved.apartment_id) propose({ apartment_id: saved.apartment_id, year: saved.year, month: saved.month, category: "lavanderia", use_operative: true, label: "Suggerito dal registro Lavanderia" }, { soloLabel: "Solo Lavanderia", silentIfNoReport: true });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveDel = async (e) => {
    e.preventDefault();
    const p = { ...formDel };
    laundryKeys.forEach((k) => { p[k] = Number(p[k]) || 0; });
    ["apartment_id", "property_id"].forEach((k) => { if (!p[k]) p[k] = null; });
    try {
      if (editDel) await http.put(`/laundry-deliveries/${editDel}`, p); else await http.post("/laundry-deliveries", p);
      toast.success("Consegna registrata"); setOpenDel(false); load(); loadConsumo();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delCost = async (id) => { if (!window.confirm("Eliminare?")) return; await http.delete(`/laundry-costs/${id}`); load(); };
  const delDel = async (id) => { if (!window.confirm("Eliminare?")) return; await http.delete(`/laundry-deliveries/${id}`); load(); loadConsumo(); };
  const uploadPdf = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("linked_type", "laundry_cost");
    const { data } = await http.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setFormCost((f) => ({ ...f, document_id: data.id })); toast.success("Fattura caricata");
  };
  const aptName = (id) => apartments.find((a) => a.id === id)?.name || "-";

  const filteredCosts = costs.filter((c) =>
    (Number(month) === c.month || tab !== "costi") && Number(year) === c.year &&
    (fApt === "all" || c.apartment_id === fApt));
  const filteredDels = deliveries.filter((d) => {
    const [y, m] = d.date.split("-");
    return Number(y) === Number(year) && Number(m) === Number(month) && (fApt === "all" || d.apartment_id === fApt);
  });

  const tabs = [
    { k: "consumo", l: "Consumo teorico" },
    { k: "costo", l: "Costo teorico" },
    { k: "consegne", l: "Consegne reali" },
    { k: "confronto", l: "Confronto" },
    { k: "ddt", l: "DDT & fattura" },
    { k: "costi", l: "Costi lavanderia" },
  ];

  return (
    <div className="space-y-6" data-testid="lavanderia-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Lavanderia</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Biancheria & Costi</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-xl min-h-[44px]" onClick={() => { setFormDel(emptyDel()); setEditDel(null); setOpenDel(true); }} data-testid="new-delivery-btn"><Plus className="h-4 w-4 mr-1" /> Consegna</Button>
          <Button className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]" onClick={() => { setFormCost(emptyCost()); setEditCost(null); setOpenCost(true); }} data-testid="new-laundry-cost-btn"><Plus className="h-4 w-4 mr-1" /> Costo</Button>
        </div>
      </div>

      <MonthChargesTable category="lavanderia" testid="charges-lavanderia" />

      <div className="flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)} className={`px-4 py-2 rounded-full text-sm font-semibold border min-h-[44px] ${tab===t.k?"bg-[var(--teal-deep)] text-white border-[var(--teal-deep)]":"bg-white text-slate-600 border-slate-200"}`}>{t.l}</button>
        ))}
      </div>

      <Card className="p-4 rounded-2xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label>Mese</Label>
            <Select value={month} onValueChange={setMonth}><SelectTrigger data-testid="lav-month"><SelectValue /></SelectTrigger>
              <SelectContent>{mesi.slice(1).map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Anno</Label>
            <Select value={year} onValueChange={setYear}><SelectTrigger data-testid="lav-year"><SelectValue /></SelectTrigger>
              <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Struttura</Label>
            <Select value={fProp} onValueChange={setFProp}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte</SelectItem>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Appartamento</Label>
            <Select value={fApt} onValueChange={setFApt}><SelectTrigger data-testid="lav-apt"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
      </Card>

      {tab === "ddt" && <LavanderiaDocs year={Number(year)} month={Number(month)} apartments={apartments} />}

      {tab === "consumo" && consumo && (
        <Card className="rounded-2xl overflow-hidden" data-testid="consumo-table">
          <div className="px-4 pt-4 text-xs text-slate-500">Stima di controllo: ogni pulizia registrata genera il consumo teorico di un set completo di biancheria.</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm mt-2">
              <thead><tr className="text-left border-b border-slate-200 text-xs uppercase text-slate-500">
                <th className="py-2 px-4">Appartamento</th><th className="px-2 text-center">Pulizie</th>
                {laundryKeys.map((k) => <th key={k} className="px-2 text-center">{laundryLabels[k]}</th>)}
              </tr></thead>
              <tbody>
                {consumo.rows.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">Nessun appartamento.</td></tr>}
                {consumo.rows.map((r) => (
                  <tr key={r.apartment_id} className="border-b border-slate-100" data-testid={`consumo-${r.apartment_id}`}>
                    <td className="py-3 px-4 font-medium">{r.apartment_name}</td>
                    <td className="px-2 text-center">{r.cleanings}</td>
                    {laundryKeys.map((k) => <td key={k} className="px-2 text-center tabular-nums">{r.theory[k]}</td>)}
                  </tr>
                ))}
                <tr className="bg-teal-50 font-bold">
                  <td className="py-3 px-4">TOTALE</td>
                  <td className="px-2 text-center">{consumo.totals.cleanings}</td>
                  {laundryKeys.map((k) => <td key={k} className="px-2 text-center tabular-nums" data-testid={`total-${k}`}>{consumo.totals[k]}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "consegne" && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="deliveries-table">
              <thead><tr className="text-left border-b border-slate-200 text-xs uppercase text-slate-500">
                <th className="py-2 px-4">Data</th><th className="px-2">Appartamento</th>
                {laundryKeys.map((k) => <th key={k} className="px-2 text-center">{laundryLabels[k]}</th>)}
                <th className="px-2"></th>
              </tr></thead>
              <tbody>
                {filteredDels.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-slate-400">Nessuna consegna nel periodo.</td></tr>}
                {filteredDels.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 tabular-nums">{fmtDate(d.date)}</td>
                    <td className="px-2">{aptName(d.apartment_id)}</td>
                    {laundryKeys.map((k) => <td key={k} className="px-2 text-center tabular-nums">{d[k]}</td>)}
                    <td className="px-2"><div className="flex gap-1 justify-end">
                      <button onClick={() => { setFormDel({ ...emptyDel(), ...d }); setEditDel(d.id); setOpenDel(true); }} className="p-1.5 text-slate-500 hover:text-teal-700"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => delDel(d.id)} className="p-1.5 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "costo" && consumo && (
        <Card className="rounded-2xl overflow-hidden" data-testid="costo-table">
          <div className="px-4 pt-4 text-xs text-slate-500">
            Stima: quantità teoriche × prezzo netto del tariffario, IVA {consumo.tariff?.vat_percent}% applicata sul totale e arrotondamento solo alla fine.
            Il valore addebitato al proprietario è quello del Rendiconto finalizzato e può legittimamente differire.
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm mt-2">
              <thead><tr className="text-left border-b border-slate-200 text-[11px] uppercase text-slate-500">
                <th className="py-2 px-4">Appartamento</th><th className="px-2 text-center">Pulizie</th>
                <th className="px-2 text-right">Netto</th><th className="px-2 text-right">IVA</th>
                <th className="px-2 text-right">Lordo previsto</th><th className="px-2 text-right">Addebitato nel rendiconto</th>
              </tr></thead>
              <tbody>
                {consumo.rows.map((r) => (
                  <tr key={r.apartment_id} className="border-b border-slate-100" data-testid={`costo-${r.apartment_id}`}>
                    <td className="py-3 px-4 font-medium">{r.apartment_name}</td>
                    <td className="px-2 text-center">{r.cleanings}</td>
                    <td className="px-2 text-right tabular-nums">{money(r.theory_cost.net)}</td>
                    <td className="px-2 text-right tabular-nums text-slate-500">{money(r.theory_cost.vat)}</td>
                    <td className="px-2 text-right tabular-nums font-semibold">{money(r.theory_cost.gross)}</td>
                    <td className="px-2 text-right tabular-nums text-[var(--teal-deep)] font-semibold">{r.report_charged == null ? "—" : money(r.report_charged)}</td>
                  </tr>
                ))}
                <tr className="bg-[color:var(--teal-light)] font-bold">
                  <td className="py-3 px-4">TOTALE</td>
                  <td className="px-2 text-center">{consumo.totals.cleanings}</td>
                  <td className="px-2 text-right tabular-nums" data-testid="costo-tot-net">{money(consumo.totals.theory_cost.net)}</td>
                  <td className="px-2 text-right tabular-nums" data-testid="costo-tot-vat">{money(consumo.totals.theory_cost.vat)}</td>
                  <td className="px-2 text-right tabular-nums" data-testid="costo-tot-gross">{money(consumo.totals.theory_cost.gross)}</td>
                  <td className="px-2 text-right tabular-nums" data-testid="costo-tot-charged">{money(consumo.totals.report_charged)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {(consumo.tariff?.items || []).filter((i) => i.active).map((i) => (
              <div key={i.key} className="flex justify-between gap-2 border border-slate-200 rounded-xl px-3 py-2">
                <span className="text-slate-500 truncate">{i.label}</span>
                <span className="tabular-nums font-semibold">{money(i.net_price)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "confronto" && consumo && (
        <>
        <Card className="rounded-2xl overflow-hidden mb-4" data-testid="confronto-costi">
          <div className="px-4 pt-4 pb-1 font-display font-bold text-[var(--teal-deep)]">Confronto costi lavanderia</div>
          <div className="px-4 pb-3 text-xs text-slate-500">Tre valori distinti: non devono coincidere. Solo il costo del Rendiconto alimenta le panoramiche economiche.</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-slate-200 text-[11px] uppercase text-slate-500">
                <th className="py-2 px-4">Appartamento</th>
                <th className="px-2 text-right">Teorico (lordo)</th>
                <th className="px-2 text-right">Reale consegne (lordo)</th>
                <th className="px-2 text-right">Addebitato nel rendiconto</th>
              </tr></thead>
              <tbody>
                {consumo.rows.map((r) => (
                  <tr key={r.apartment_id} className="border-b border-slate-100" data-testid={`confronto-costo-${r.apartment_id}`}>
                    <td className="py-3 px-4 font-medium">{r.apartment_name}</td>
                    <td className="px-2 text-right tabular-nums">{money(r.theory_cost.gross)}</td>
                    <td className="px-2 text-right tabular-nums">{money(r.real_cost.gross)}</td>
                    <td className="px-2 text-right tabular-nums font-semibold text-[var(--teal-deep)]">{r.report_charged == null ? "—" : money(r.report_charged)}</td>
                  </tr>
                ))}
                <tr className="bg-[color:var(--teal-light)] font-bold">
                  <td className="py-3 px-4">TOTALE</td>
                  <td className="px-2 text-right tabular-nums">{money(consumo.totals.theory_cost.gross)}</td>
                  <td className="px-2 text-right tabular-nums">{money(consumo.totals.real_cost.gross)}</td>
                  <td className="px-2 text-right tabular-nums">{money(consumo.totals.report_charged)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
        <Card className="rounded-2xl overflow-hidden" data-testid="confronto-table">
          <div className="px-4 pt-4 text-xs text-slate-500">Lo scostamento è solo un indicatore: non genera contestazioni né modifiche contabili.</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm mt-2">
              <thead><tr className="text-left border-b border-slate-200 text-xs uppercase text-slate-500">
                <th className="py-2 px-4">Appartamento</th><th className="px-2">Voce</th>
                <th className="px-2 text-right">Teorico</th><th className="px-2 text-right">Consegnato</th><th className="px-2 text-right">Scostamento</th>
              </tr></thead>
              <tbody>
                {consumo.rows.map((r) => laundryKeys.map((k) => (
                  <tr key={`${r.apartment_id}-${k}`} className="border-b border-slate-100">
                    <td className="py-2 px-4 font-medium">{r.apartment_name}</td>
                    <td className="px-2 text-xs">{laundryLabels[k]}</td>
                    <td className="px-2 text-right tabular-nums">{r.theory[k]}</td>
                    <td className="px-2 text-right tabular-nums">{r.real[k]}</td>
                    <td className={`px-2 text-right tabular-nums font-semibold ${r.diff[k] > 0 ? "text-amber-700" : r.diff[k] < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {r.diff[k] > 0 ? "+" : ""}{r.diff[k]}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </Card>
        </>
      )}

      {tab === "costi" && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="laundry-costs-table">
              <thead><tr className="text-left border-b border-slate-200 text-xs uppercase text-slate-500">
                <th className="py-2 px-4">Mese</th><th className="px-2">Appartamento</th><th className="px-2">Fornitore</th>
                <th className="px-2 text-right">Importo</th><th className="px-2">A carico</th><th className="px-2">Pagato</th><th className="px-2"></th>
              </tr></thead>
              <tbody>
                {filteredCosts.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">Nessun costo registrato.</td></tr>}
                {filteredCosts.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`laundry-cost-${c.id}`}>
                    <td className="py-3 px-4">{mesi[c.month]} {c.year}</td>
                    <td className="px-2">{aptName(c.apartment_id)}</td>
                    <td className="px-2">{c.supplier || "-"}</td>
                    <td className="px-2 text-right tabular-nums font-semibold">{money(c.amount)}</td>
                    <td className="px-2"><Badge variant="outline" className={c.charged_to === "proprietario" ? "border-teal-300 text-teal-800" : "border-slate-300 text-slate-600"}>{c.charged_to === "proprietario" ? "Proprietario" : "PuraVida"}</Badge></td>
                    <td className="px-2">{c.paid ? <Badge className="bg-emerald-100 text-emerald-800">Pagato</Badge> : <Badge variant="outline" className="border-amber-300 text-amber-800">No</Badge>}</td>
                    <td className="px-2"><div className="flex gap-1 justify-end">
                      {c.document_id && <a href={fileUrl(c.document_id)} target="_blank" rel="noreferrer" className="p-1.5 text-slate-500 hover:text-teal-700"><Paperclip className="h-4 w-4" /></a>}
                      <button onClick={() => { setFormCost({ ...emptyCost(), ...c }); setEditCost(c.id); setOpenCost(true); }} className="p-1.5 text-slate-500 hover:text-teal-700"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => delCost(c.id)} className="p-1.5 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={openCost} onOpenChange={setOpenCost}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editCost ? "Modifica" : "Nuovo"} costo lavanderia</DialogTitle></DialogHeader>
          <form onSubmit={saveCost} className="grid grid-cols-2 gap-4" data-testid="laundry-cost-form">
            <div><Label>Mese</Label>
              <Select value={String(formCost.month)} onValueChange={(v) => setFormCost({ ...formCost, month: Number(v) })}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{mesi.slice(1).map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Anno</Label><Input type="number" value={formCost.year} onChange={(e) => setFormCost({ ...formCost, year: e.target.value })} /></div>
            <div className="col-span-2"><Label>Appartamento</Label>
              <Select value={formCost.apartment_id || ""} onValueChange={(v) => { const a = apartments.find((x) => x.id === v); setFormCost({ ...formCost, apartment_id: v, property_id: a?.property_id || formCost.property_id }); }}>
                <SelectTrigger data-testid="lc-apt"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Fornitore</Label><Input value={formCost.supplier || ""} onChange={(e) => setFormCost({ ...formCost, supplier: e.target.value })} /></div>
            <div><Label>Importo (€)</Label><Input type="number" step="0.01" value={formCost.amount} onChange={(e) => setFormCost({ ...formCost, amount: e.target.value })} data-testid="lc-amount" /></div>
            <div className="flex items-center gap-3 pt-6"><Switch checked={formCost.paid} onCheckedChange={(v) => setFormCost({ ...formCost, paid: v })} /><Label>Pagato</Label></div>
            <p className="md:col-span-2 text-xs text-slate-500">Dopo il salvataggio ti chiederò se aggiornare anche il Rendiconto del mese (anteprima del nuovo netto). Il Rendiconto vince finché non confermi.</p>
            <div><Label>A carico di</Label>
              <Select value={formCost.charged_to} onValueChange={(v) => setFormCost({ ...formCost, charged_to: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="proprietario">Proprietario</SelectItem><SelectItem value="puravida">PuraVida Home</SelectItem></SelectContent></Select>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3"><Switch checked={formCost.considera_rendiconto} onCheckedChange={(v) => setFormCost({ ...formCost, considera_rendiconto: v })} data-testid="lc-considera" /><Label className="text-xs">Considera nel rendiconto</Label></div>
              <div className="flex items-center gap-3"><Switch checked={formCost.mostra_pdf} onCheckedChange={(v) => setFormCost({ ...formCost, mostra_pdf: v })} data-testid="lc-showpdf" /><Label className="text-xs">Mostra nel PDF</Label></div>
            </div>
            <div className="col-span-2"><Label>PDF fattura</Label>
              <Input type="file" accept="application/pdf,image/*" onChange={(e) => e.target.files?.[0] && uploadPdf(e.target.files[0])} />
              {formCost.document_id && <div className="text-xs text-emerald-600 mt-1">✓ Fattura allegata</div>}
            </div>
            <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={formCost.notes || ""} onChange={(e) => setFormCost({ ...formCost, notes: e.target.value })} /></div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpenCost(false)}>Annulla</Button>
              <Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="lc-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={openDel} onOpenChange={setOpenDel}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editDel ? "Modifica" : "Nuova"} consegna lavanderia</DialogTitle></DialogHeader>
          <form onSubmit={saveDel} className="grid grid-cols-2 gap-4" data-testid="delivery-form">
            <div><Label>Data</Label><Input type="date" value={formDel.date} onChange={(e) => setFormDel({ ...formDel, date: e.target.value })} required /></div>
            <div><Label>Appartamento</Label>
              <Select value={formDel.apartment_id || ""} onValueChange={(v) => { const a = apartments.find((x) => x.id === v); setFormDel({ ...formDel, apartment_id: v, property_id: a?.property_id || formDel.property_id }); }}>
                <SelectTrigger data-testid="del-apt"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
            </div>
            {laundryKeys.map((k) => (
              <div key={k}><Label>{laundryLabels[k]}</Label>
                <Input type="number" min="0" value={formDel[k]} onChange={(e) => setFormDel({ ...formDel, [k]: e.target.value })} data-testid={`del-${k}`} />
              </div>
            ))}
            <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={formDel.notes || ""} onChange={(e) => setFormDel({ ...formDel, notes: e.target.value })} /></div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpenDel(false)}>Annulla</Button>
              <Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="del-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {linkDialog}
    </div>
  );
}
